import { IconInfoItalicOutlined } from "@suse-o11y/icons";
import { type Signal, useMonitor } from "@suse-o11y/signal";
import React from "react";

import { RefreshIntervalSelector } from "~/components/refresh-interval-selector";
import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import { Truncated } from "~/components/truncated";

import { HealthTimelineChart } from "../../../../components/_health-timeline-chart";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { TimelineRangeSelectorExtended } from "../../../../components/react/timeline/TimelineRangeSelectorExtended";
import { queryMonitorCheckStatusHealthHistory } from "../../../../queries";
import { useQuery } from "../../../../queries/react-query";
import { useRefetchQueriesInNamespace } from "../../../../queries/use-refetch-queries-in-namespace";

import { useMonitorStatusDrawerContext } from "./MonitorStatusDrawer/use-monitor-status-drawer-context";
import { MONITOR_STATUS_DRAWER_QUERY_NAMESPACE } from "./_query-namespace";

export interface MonitorHealthTimelineProps {
	identifier: string;
	disabled?: boolean;
	action?: React.ReactNode;
	onRefresh: VoidFunction;
	refreshIntervalSignal: Signal<RefreshInterval>;
}

export const MonitorHealthTimeline: React.FC<MonitorHealthTimelineProps> = ({
	identifier,
	disabled = false,
	action,
	onRefresh,
	refreshIntervalSignal,
}) => {
	const monitor = useMonitor();
	const { timestamp, timeRange } = useMonitorStatusDrawerContext();
	const timeRangeValue = timeRange.read(monitor);
	const isTimestampInRange = timeRangeValue.contains(timestamp.read(monitor));
	const [isRefetching] = useRefetchQueriesInNamespace(MONITOR_STATUS_DRAWER_QUERY_NAMESPACE);
	const refreshInterval = refreshIntervalSignal.read(monitor);

	const { data, isLoading } = useQuery({
		namespace: MONITOR_STATUS_DRAWER_QUERY_NAMESPACE,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		...queryMonitorCheckStatusHealthHistory({
			identifier,
			timeRange: timeRangeValue,
		}),
	});

	return (
		<Widget className="pb-3">
			<WidgetHeader className="flex flex-wrap justify-between gap-y-4" size="xl" title="Monitor health timeline">
				<div className="flex flex-row flex-wrap items-center justify-end gap-4">
					{!isTimestampInRange && (
						<Truncated className="ml-auto flex select-none items-center gap-1 font-light text-grey-600 italic" el="div">
							<IconInfoItalicOutlined className="inline-block text-(--base-blue-600)" />
							<span className="text-13">The monitor status time is outside the selected interval</span>
						</Truncated>
					)}

					<TimelineRangeSelectorExtended
						contextName="monitor status"
						disabled={isLoading || disabled}
						timeRange={timeRange}
					/>

					<RefreshIntervalSelector
						disabled={timeRangeValue.isSpecific()}
						loading={isRefetching}
						onChange={(newInterval) => {
							refreshIntervalSignal.write(newInterval);
						}}
						onRefresh={onRefresh}
						value={refreshInterval}
					/>

					{action}
				</div>
			</WidgetHeader>

			<WidgetBody card className="px-10 py-3">
				<HealthTimelineChart
					disabled={disabled}
					healthStateChanges={data?.healthStateChanges}
					muteIntervals={data?.muteIntervals}
					timeRange={timeRange}
					timestamp={timestamp}
				/>
			</WidgetBody>
		</Widget>
	);
};
